import { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

// Module-level cache — survives component remounts within a session
const progressCache = {};

const C = {
  navyDark:    "#f4f5f7",
  navyMid:     "#e8eaed",
  navyBorder:  "#d0d4da",
  green:       "#75BB2E",
  greenFaint:  "rgba(117,187,46,0.1)",
  greenBorder: "rgba(117,187,46,0.35)",
  textPrimary: "#071D39",
  textMuted:   "#516469",
  textDim:     "#89A8B1",
};

const TYPE_COLORS = {
  "Actions":                 { bg: "rgba(96,165,250,0.15)",  border: "rgba(96,165,250,0.4)",  text: "#2563eb" },
  "Materials":               { bg: "rgba(251,191,36,0.15)",  border: "rgba(251,191,36,0.4)",  text: "#b45309" },
  "Communication":           { bg: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.4)", text: "#7c3aed" },
  "Pre-Appointment Package": { bg: "rgba(251,146,60,0.15)",  border: "rgba(251,146,60,0.4)",  text: "#c2410c" },
};

const CHEAT_SHEETS = [
  {
    id: "creating-a-cma",
    title: "Creating a CMA",
    icon: "📊",
    description: "Step-by-step checklist for creating a Comparative Market Analysis",
    steps: [
      {
        order: "1",
        type: "Actions",
        description: "Check property appraiser for all owners on title",
        notes: "Make sure all owners are aware and in contact with you",
        subSteps: [],
      },
      {
        order: "2",
        type: "Actions",
        description: "Check RPR, Zillow, Realtor.com — also have an idea of what seller thinks it's worth",
        notes: "RPR very useful to pull CMA rather than MLS",
        subSteps: [],
      },
      {
        order: "3",
        type: "Actions",
        description: "Permit Search — gather info for improvements to property",
        notes: "",
        subSteps: [],
      },
      {
        order: "4",
        type: "Actions",
        description: "In MLS, use the below criteria",
        notes: "",
        subSteps: [
          { order: "4.1", description: "Status = Active, Active under Contract, Pending, Closed" },
          { order: "4.2", description: "Property SubType = Subject Property (ie Townhome to Townhome)" },
          { order: "4.3", description: "Use Radius feature and go .25 miles (if necessary widen to .5 mil)" },
          { order: "4.4", description: "Sq Footage = s/b 400 sq ft less to 400 sq ft more than subject" },
        ],
      },
      {
        order: "5",
        type: "Actions",
        description: "Look at Comps (lot size, how long on market, age of home)",
        notes: "",
        subSteps: [],
      },
      {
        order: "6",
        type: "Actions",
        description: "If it has a pool, adjust 20-25k",
        notes: "",
        subSteps: [],
      },
      {
        order: "7",
        type: "Actions",
        description: "For every add'l BR, add 10-15k; for every BR short, subtract 10-15k",
        notes: "",
        subSteps: [],
      },
    ],
  },
  {
    id: "open-house",
    title: "Open House",
    icon: "🏡",
    description: "Materials checklist and step-by-step actions for a successful open house",
    steps: [
      {
        order: "M",
        type: "Materials",
        description: "Gather all materials before the open house",
        notes: "",
        subSteps: [
          { order: "M1",  description: "Flyer Insert" },
          { order: "M2",  description: "Carafe (Raffle holder if doing a paper raffle)" },
          { order: "M3",  description: "Stand up (holds pens, business cards, raffles)" },
          { order: "M4",  description: "MLS Printout" },
          { order: "M5",  description: "Pens" },
          { order: "M6",  description: "Tray for Snacks" },
          { order: "M7",  description: "Snacks/Water/Cookies" },
          { order: "M8",  description: "Gift Card" },
          { order: "M9",  description: "Broom/Dustpan" },
          { order: "M10", description: "Trash Bags" },
          { order: "M11", description: "Clorox Wipes" },
          { order: "M12", description: "Toilet Paper" },
          { order: "M13", description: "Glade Plug-ins" },
        ],
      },
      {
        order: "1",
        type: "Actions",
        description: "Create design and share on reel",
        notes: "",
        subSteps: [],
      },
      {
        order: "2",
        type: "Actions",
        description: "Create flyer for insert and add'l copies (include QR code to listing)",
        notes: "Send to Walgreens for printing",
        subSteps: [],
      },
      {
        order: "3",
        type: "Actions",
        description: "Print MLS listing (2 copies, one for placard, one for you to review/highlight)",
        notes: "Send to Walgreens for printing",
        subSteps: [],
      },
      {
        order: "4",
        type: "Actions",
        description: "Update MLS listing with times of Open House",
        notes: "If not your listing, ask agent to do it",
        subSteps: [],
      },
      {
        order: "5",
        type: "Actions",
        description: "Ask agent if Supra iboxx or what lock box code is",
        notes: "",
        subSteps: [],
      },
      {
        order: "6",
        type: "Actions",
        description: "Put signs at all major intersections of neighborhood and continue to direct via arrows",
        notes: "",
        subSteps: [],
      },
      {
        order: "7",
        type: "Actions",
        description: "Put large Open House sign in Yard (balloons optional)",
        notes: "",
        subSteps: [],
      },
      {
        order: "8",
        type: "Actions",
        description: "Set everything out (snacks, drinks, cards, etc)",
        notes: "",
        subSteps: [],
      },
      {
        order: "9",
        type: "Actions",
        description: "Turn on every light",
        notes: "",
        subSteps: [],
      },
      {
        order: "10",
        type: "Actions",
        description: "Put TP in the closest bathroom to kitchen",
        notes: "",
        subSteps: [],
      },
      {
        order: "11",
        type: "Actions",
        description: "Wipe down kitchen counter (if needed)",
        notes: "",
        subSteps: [],
      },
      {
        order: "12",
        type: "Actions",
        description: "Sweep entry way and tidy up anything out of place",
        notes: "",
        subSteps: [],
      },
      {
        order: "13",
        type: "Actions",
        description: "Check that A/C set to reasonable temp",
        notes: "",
        subSteps: [],
      },
      {
        order: "14",
        type: "Actions",
        description: "Bring up personal website and at end of URL, type \"/openhouse\"",
        notes: "",
        subSteps: [],
      },
      {
        order: "15",
        type: "Actions",
        description: "Ask guests to sign in on iPad to register for a chance to win the GC",
        notes: "",
        subSteps: [],
      },
      {
        order: "16",
        type: "Actions",
        description: "Engage with guests, answer questions, ask if working with agent",
        notes: "",
        subSteps: [],
      },
      {
        order: "17",
        type: "Actions",
        description: "Pack everything back in bin (Glade plugins, TP, all materials)",
        notes: "",
        subSteps: [],
      },
      {
        order: "18",
        type: "Actions",
        description: "Turn off all lights, set A/C back to what it originally was, lock up",
        notes: "",
        subSteps: [],
      },
      {
        order: "19",
        type: "Actions",
        description: "Pull up all open house signs",
        notes: "",
        subSteps: [],
      },
      {
        order: "20",
        type: "Actions",
        description: "Text listing agent and confirm house has been closed down and locked — thank them for the opportunity",
        notes: "",
        subSteps: [],
      },
      {
        order: "21",
        type: "Actions",
        description: "Make sure all guest info has been put into CRM accurately. Set up relevant search alerts, etc",
        notes: "",
        subSteps: [],
      },
      {
        order: "22",
        type: "Communication",
        description: "Text all guests that left a phone number (DAY OF OPEN HOUSE) that it was great meeting them and share your contact card",
        notes: "",
        subSteps: [],
      },
      {
        order: "23",
        type: "Communication",
        description: "Follow up with all leads within 2-3 days to see if you can 1) create a search alert for them, 2) ask if there's anything you can help with, etc",
        notes: "",
        subSteps: [],
      },
    ],
  },
];

export default function CheatSheetsTab({ projectId }) {
  const [activeSheet, setActiveSheet] = useState(null);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);

    if (progressCache[projectId] !== undefined) {
      setChecked(progressCache[projectId]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const ref = doc(db, "cheatSheetProgress", projectId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        const data = snap.exists() ? (snap.data().checked || {}) : {};
        progressCache[projectId] = data;
        setChecked(data);
      } catch (e) {
        if (!cancelled) setChecked({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const saveChecked = (newChecked) => {
    if (!projectId) return;
    progressCache[projectId] = newChecked;
    const ref = doc(db, "cheatSheetProgress", projectId);
    setDoc(ref, { checked: newChecked }, { merge: true });
  };

  const toggleCheck = (id, subSteps = [], sheetId = "") => {
    setChecked(prev => {
      const newVal = !prev[id];
      const next = { ...prev, [id]: newVal };
      subSteps.forEach(sub => { next[`${sheetId}-step-${sub.order}`] = newVal; });
      saveChecked(next);
      return next;
    });
  };

  const toggleSubCheck = (subId, parentId, allSubIds) => {
    setChecked(prev => {
      const next = { ...prev, [subId]: !prev[subId] };
      const allDone = allSubIds.every(sid => sid === subId ? !prev[subId] : !!prev[sid]);
      next[parentId] = allDone;
      saveChecked(next);
      return next;
    });
  };

  const handleReset = () => {
    const empty = {};
    progressCache[projectId] = empty;
    setChecked(empty);
    saveChecked(empty);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
        Loading...
      </div>
    );
  }

  if (activeSheet) {
    const sheet = CHEAT_SHEETS.find(s => s.id === activeSheet);
    const totalItems = sheet.steps.reduce((acc, s) => acc + 1 + s.subSteps.length, 0);
    const checkedItems = Object.keys(checked).filter(k => k.startsWith(sheet.id) && checked[k]).length;
    const pct = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

    return (
      <div style={{ paddingBottom: 32 }}>
        <div style={{
          backgroundColor: C.navyDark, borderBottom: `1px solid ${C.navyBorder}`,
          padding: "16px", display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => setActiveSheet(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.textMuted, fontSize: 20 }}
            >←</button>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary }}>
                {sheet.icon} {sheet.title}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{sheet.description}</div>
            </div>
          </div>
          <button
            onClick={handleReset}
            style={{
              padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700,
              background: C.greenFaint, border: `1px solid ${C.greenBorder}`,
              color: C.green, cursor: "pointer", fontFamily: "inherit",
            }}
          >Reset</button>
        </div>

        <div style={{ padding: "12px 16px", backgroundColor: C.navyMid }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>PROGRESS</span>
            <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>{checkedItems}/{totalItems} ({pct}%)</span>
          </div>
          <div style={{ height: 6, backgroundColor: "rgba(0,0,0,0.08)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${pct}%`,
              backgroundColor: pct === 100 ? C.green : "#60a5fa",
              borderRadius: 3, transition: "width 0.3s ease",
            }} />
          </div>
        </div>

        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {sheet.steps.map((step) => {
            const stepId = `${sheet.id}-step-${step.order}`;
            const isChecked = !!checked[stepId];
            const typeStyle = TYPE_COLORS[step.type] || TYPE_COLORS["Actions"];

            return (
              <div key={step.order}>
                <div
                  onClick={() => toggleCheck(stepId, step.subSteps, sheet.id)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "12px 14px",
                    backgroundColor: isChecked ? "rgba(117,187,46,0.08)" : C.navyDark,
                    border: `1px solid ${isChecked ? C.greenBorder : C.navyBorder}`,
                    borderRadius: step.subSteps.length > 0 ? "10px 10px 0 0" : 10,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                    backgroundColor: isChecked ? C.green : "transparent",
                    border: `2px solid ${isChecked ? C.green : "#89A8B1"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s",
                  }}>
                    {isChecked && <span style={{ fontSize: 13, color: "#fff", fontWeight: 900 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                        backgroundColor: typeStyle.bg, border: `1px solid ${typeStyle.border}`, color: typeStyle.text,
                      }}>{step.type}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.textDim }}>
                        {step.order === "M" ? "MATERIALS" : `STEP ${step.order}`}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 600, lineHeight: 1.4,
                      color: isChecked ? C.textMuted : C.textPrimary,
                      textDecoration: isChecked ? "line-through" : "none",
                    }}>{step.description}</div>
                    {step.notes && (
                      <div style={{
                        marginTop: 6, fontSize: 11, color: C.textMuted,
                        borderLeft: `2px solid ${C.green}`, padding: "4px 8px",
                        borderRadius: "0 4px 4px 0", backgroundColor: "rgba(0,0,0,0.03)",
                      }}>💡 {step.notes}</div>
                    )}
                  </div>
                </div>

                {step.subSteps.length > 0 && (
                  <div style={{
                    border: `1px solid ${C.navyBorder}`, borderTop: "none",
                    borderRadius: "0 0 10px 10px", overflow: "hidden",
                  }}>
                    {step.subSteps.map((sub, i) => {
                      const subId = `${sheet.id}-step-${sub.order}`;
                      const subChecked = !!checked[subId];
                      return (
                        <div
                          key={sub.order}
                          onClick={() => toggleSubCheck(subId, stepId, step.subSteps.map(s => `${sheet.id}-step-${s.order}`))}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "10px 14px 10px 48px",
                            backgroundColor: subChecked ? "rgba(117,187,46,0.08)" : C.navyMid,
                            borderBottom: i < step.subSteps.length - 1 ? `1px solid ${C.navyBorder}` : "none",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                            backgroundColor: subChecked ? C.green : "transparent",
                            border: `2px solid ${subChecked ? C.green : "#89A8B1"}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {subChecked && <span style={{ fontSize: 11, color: "#fff", fontWeight: 900 }}>✓</span>}
                          </div>
                          <div>
                            <span style={{ fontSize: 10, color: C.textDim, fontWeight: 700, marginRight: 8 }}>{sub.order}</span>
                            <span style={{
                              fontSize: 12,
                              color: subChecked ? C.textMuted : C.textPrimary,
                              textDecoration: subChecked ? "line-through" : "none",
                            }}>{sub.description}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {pct === 100 && (
          <div style={{
            margin: "0 16px", padding: "16px",
            backgroundColor: C.greenFaint, border: `1px solid ${C.greenBorder}`,
            borderRadius: 10, textAlign: "center",
          }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>🎉</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>Complete!</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CHEAT_SHEETS.map(sheet => {
          const totalItems = sheet.steps.reduce((acc, s) => acc + 1 + s.subSteps.length, 0);
          const checkedItems = Object.keys(checked).filter(k => k.startsWith(sheet.id) && checked[k]).length;
          const pct = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

          return (
            <div
              key={sheet.id}
              onClick={() => setActiveSheet(sheet.id)}
              style={{
                display: "flex", alignItems: "center", gap: 14, padding: "16px",
                backgroundColor: C.navyDark, border: `1px solid ${C.navyBorder}`,
                borderRadius: 10, cursor: "pointer", transition: "border-color 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.green}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.navyBorder}
            >
              <div style={{ fontSize: 32 }}>{sheet.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>{sheet.title}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{sheet.description}</div>
                <div style={{ fontSize: 11, color: pct > 0 ? C.green : C.textDim, marginTop: 4, fontWeight: pct > 0 ? 700 : 400 }}>
                  {pct > 0 ? `${checkedItems}/${totalItems} steps complete (${pct}%)` : `${totalItems} steps`}
                </div>
              </div>
              <div style={{ color: C.textMuted, fontSize: 18 }}>→</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}